import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { FullPageMessage } from '@/components/FullPageMessage'
import { Button } from '@/components/ui'

/**
 * Where Microsoft sign-in comes back to. Invitation and reset emails now go
 * to /auth/confirm instead (see AuthConfirm); the invite/recovery handling
 * below only catches links from emails sent before the send-email-hook.
 *
 * supabase-js is configured with detectSessionInUrl, so by the time this
 * mounts the code in the URL has usually already been exchanged. This screen
 * exists to wait for that, strip the code from the address bar, and give a
 * clear way out when the exchange fails, rather than leaving someone on a
 * blank page holding a URL full of tokens.
 */
export function AuthCallback() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function finish() {
      // Supabase returns success codes on the query string but returns errors
      // on the URL fragment (#error=...&error_description=...). Read both.
      const query = new URLSearchParams(window.location.search)
      const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''))
      const oauthError =
        fragment.get('error_description') ??
        query.get('error_description') ??
        fragment.get('error') ??
        query.get('error')
      const errorCode = fragment.get('error_code') ?? query.get('error_code')

      // The flow type is on the fragment for token flows (invite, recovery,
      // magiclink, signup) and is our reliable signal to route to /set-password.
      // Read it BEFORE calling getSession, because detectSessionInUrl clears
      // the fragment as a side effect of consuming it.
      const flowType = fragment.get('type')
      if (oauthError) {
        // The most common cause of "otp_expired" on an invite is Gmail's link
        // scanner using the token before the human clicks. Name that plainly
        // so the response is "resend the invite" not "email is broken".
        if (errorCode === 'otp_expired') {
          setError(
            'That invitation link has already been used or has expired. This often happens ' +
              'when an email scanner opens the link before you do. Ask for a fresh invitation.',
          )
        } else {
          setError(oauthError)
        }
        return
      }

      const { data, error } = await supabase.auth.getSession()
      if (cancelled) return

      if (error || !data.session) {
        setError('That sign-in link could not be completed. It may have already been used.')
        return
      }

      // Invitations and password resets both land here; both need the user
      // to set (or reset) their password. Everything else (Microsoft OAuth,
      // subsequent sign-ins) goes to the home page.
      const needsPasswordSetup = flowType === 'invite' || flowType === 'recovery'

      window.history.replaceState({}, '', '/auth/callback')
      navigate(needsPasswordSetup ? '/set-password' : '/', { replace: true })
    }

    void finish()
    return () => {
      cancelled = true
    }
  }, [navigate])

  if (error) {
    return (
      <FullPageMessage title="Sign-in did not complete" body={error}>
        <Button onClick={() => navigate('/sign-in', { replace: true })}>Back to sign in</Button>
      </FullPageMessage>
    )
  }

  return <FullPageMessage title="Finishing sign-in" body="One moment." />
}
